import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useDeleteSettingModuleMutation } from "entities/brands/settings/mutations/useDeleteSettingModuleMutation";
import { useReorderSettingModulesMutation } from "entities/brands/settings/mutations/useReorderSettingModulesMutation";
import { invalidateModuleListByBrandSettingQuery } from "entities/brands/settings/queries/useModuleListByBrandSettingQuery";
import { invalidateEntireModuleListQuery } from "entities/modules/queries/useModuleListByTagIdQuery";
import { PageModuleData } from "entities/pages/types";
import { FC, useState } from "react";
import DeleteConfirmationModal from "shared/components/DeleteConfirmationModal";
import DragNDropTable from "shared/components/DragNDropTable";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import PageHeading from "shared/components/PageHeading";
import PrimaryButton from "shared/components/PrimaryButton";
import { useModalState } from "shared/hooks/useModalState";
import ModuleDetailsDrawerContainer from "./components/ModuleDetailsDrawer";
import PasteModulePopup from "./components/PasteModulePopup";
import { SettingsModulesProvider, useSettingsModules } from "./context";
import { getPageModulesColumns, getTextForDeletingPopup } from "./helpers";
import { ColumnsProps } from "./types";

const SettingsModulesContainer = () => (
  <SettingsModulesProvider>
    <SettingModules />
  </SettingsModulesProvider>
);

const SettingModules: FC = () => {
  const pasteModal = useModalState();
  const [deletableModule, setDeletableModule] = useState<PageModuleData | null>(
    null,
  );
  const deleteModalState = useModalState();
  const { mutate: mutateDeleteModule, isPending: isDeletingModule } =
    useDeleteSettingModuleMutation();

  const {
    isLoadingPages,
    modulePageTitle,
    moduleDetailsDrawer,
    onUpdateStatus,
    brandId,
    settingId,
    searchData,
  } = useSettingsModules();

  const writeAccess = useRolePermission("brandsAndPages:write");
  const { mutate: mutateReorder, isPending: isPendingReorder } =
    useReorderSettingModulesMutation();

  const handleReorderSuccess = () => {
    invalidateModuleListByBrandSettingQuery(brandId, settingId);
  };

  const handleDeleteModuleClick = (moduleData: PageModuleData) => {
    setDeletableModule(moduleData);
    deleteModalState.openModal();
  };

  const handleDeleteModuleConfirm = () => {
    if (!deletableModule) {
      return;
    }

    mutateDeleteModule({
      brandId,
      settingId,
      moduleId: deletableModule.id,
      onSuccess: deleteModalState.closeModal,
    });
    invalidateEntireModuleListQuery();
  };

  const columnsParams: ColumnsProps = {
    moduleDetailsDrawer: moduleDetailsDrawer,
    handleStatus: onUpdateStatus,
    onDeleteClick: handleDeleteModuleClick,
    readOnly: !writeAccess.allowed,
  };

  const columns = getPageModulesColumns(columnsParams);

  const handleReorder = (modules: PageModuleData[]) => {
    const formattedModules = modules.map((module, idx) => ({
      ...module,
      order: idx + 1,
    }));

    mutateReorder(
      {
        brandId,
        settingId,
        modules: formattedModules,
      },
      { onSuccess: handleReorderSuccess },
    );
  };

  return (
    <FlexColumn>
      <PageHeading
        customPageTitle={modulePageTitle}
        additionalLabel={" (Settings)"}
      >
        {writeAccess.allowed && (
          <FlexCenter gap="1rem">
            <PrimaryButton
              title="Insert Module"
              size="small"
              color="secondary"
              variant="outlined"
              rounded={false}
              onClick={pasteModal.openModal}
            />
            <PrimaryButton
              title={"Add new Module"}
              onClick={() => moduleDetailsDrawer.openDrawer()}
              size="small"
            />
          </FlexCenter>
        )}
      </PageHeading>

      <DragNDropTable<PageModuleData>
        items={searchData.filteredData || []}
        columns={columns}
        reorderDisabled={!writeAccess.allowed || searchData.isSearchActive}
        isItemsFetched={!isLoadingPages}
        isItemsLoading={isLoadingPages || isPendingReorder}
        noItemsMessage="No modules found"
        emptyListMessage="No modules found"
        onItemsReorder={handleReorder}
        onRowClick={(params) => moduleDetailsDrawer.openDrawer(params)}
      />
      <ModuleDetailsDrawerContainer />
      <PasteModulePopup
        open={pasteModal.isOpen}
        onClose={pasteModal.closeModal}
      />
      <DeleteConfirmationModal
        title={`Delete "${deletableModule?.name ?? ""}" Module`}
        content={
          deletableModule ? getTextForDeletingPopup(deletableModule) : ""
        }
        applyButtonTitle="Yes, delete"
        onClose={deleteModalState.closeModal}
        open={deleteModalState.isOpen}
        handleSuccess={handleDeleteModuleConfirm}
        applyButtonLoading={isDeletingModule}
      />
    </FlexColumn>
  );
};

export default SettingsModulesContainer;
